import React from 'react';
import { SEO } from '../../components/seo/SEO';
import { Shield, Lock, Database, Eye, Cookie, Bell, UserCheck, Trash2, BarChart } from 'lucide-react';
import { PageLayout } from '../../components/layout/PageLayout';
import { useTranslation } from '../../hooks/useTranslation';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';

export function Privacy() {
  const { t } = useTranslation();
  const { getPathForLang } = useI18n();
  
  // Définition des items des listes (version fallback en cas d'erreur)
  const dataCollectedItems = [
    t('privacy.data_collected.items.0'),
    t('privacy.data_collected.items.1'),
    t('privacy.data_collected.items.2'),
    t('privacy.data_collected.items.3')
  ];
  
  const dataUsageItems = [
    t('privacy.data_usage.items.0'),
    t('privacy.data_usage.items.1'),
    t('privacy.data_usage.items.2'),
    t('privacy.data_usage.items.3')
  ];
  
  const dataProtectionItems = [
    t('privacy.data_protection.items.0'),
    t('privacy.data_protection.items.1'),
    t('privacy.data_protection.items.2'),
    t('privacy.data_protection.items.3')
  ];
  
  const cookiesEssentialItems = [
    t('privacy.cookies.essential_items.0'),
    t('privacy.cookies.essential_items.1'),
    t('privacy.cookies.essential_items.2')
  ];
  
  const cookiesThirdPartyItems = [
    t('privacy.cookies.third_party_items.0'),
    t('privacy.cookies.third_party_items.1'),
    t('privacy.cookies.third_party_items.2')
  ];
  
  const advertisingItems = [
    t('privacy.advertising.items.0'),
    t('privacy.advertising.items.1'),
    t('privacy.advertising.items.2'),
    t('privacy.advertising.items.3')
  ];
  
  const rightsItems = [
    t('privacy.rights.items.0'),
    t('privacy.rights.items.1'),
    t('privacy.rights.items.2'),
    t('privacy.rights.items.3')
  ];
  
  return (
    <PageLayout disableAds={true}>
      <SEO
        title={t('privacy.seo.title')}
        description={t('privacy.seo.description')}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{t('privacy.title')}</h1>
            <p className="text-lg text-gray-600">
              {t('privacy.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="p-8">
              <div className="prose prose-blue max-w-none">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
                  <div className="flex items-center mb-4">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="ml-4 text-2xl font-semibold text-gray-900">{t('privacy.commitment.title')}</h2>
                  </div>
                  <p className="text-gray-600">
                    {t('privacy.commitment.description')}
                  </p>
                </div>

                <div className="grid gap-8 mb-12">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Database className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.data_collected.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>{t('privacy.data_collected.intro')}</p>
                      <ul className="list-disc list-inside space-y-2">
                        {dataCollectedItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Eye className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.data_usage.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>{t('privacy.data_usage.intro')}</p>
                      <ul className="list-disc list-inside space-y-2">
                        {dataUsageItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Lock className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.data_protection.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('privacy.data_protection.intro')}
                      </p>
                      <ul className="list-disc list-inside space-y-2">
                        {dataProtectionItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <Cookie className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.cookies.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('privacy.cookies.essential_intro')}
                      </p>
                      <ul className="list-disc list-inside space-y-2">
                        {cookiesEssentialItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                      <p className="mt-4">
                        {t('privacy.cookies.third_party_intro')}
                      </p>
                      <ul className="list-disc list-inside space-y-2">
                        {cookiesThirdPartyItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <BarChart className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.advertising.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>
                        {t('privacy.advertising.intro')}
                      </p>
                      <ul className="list-disc list-inside space-y-2">
                        {advertisingItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                      <p className="mt-4">
                        {t('privacy.advertising.more_info')}{' '}
                        <a href="https://policies.google.com/technologies/partner-sites" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-blue-600 hover:text-blue-800">
                          {t('privacy.advertising.google_privacy')}
                        </a>.
                      </p>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <UserCheck className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="ml-4 text-lg font-semibold text-gray-900">{t('privacy.rights.title')}</h3>
                    </div>
                    <div className="space-y-3 text-gray-600">
                      <p>{t('privacy.rights.intro')}</p>
                      <ul className="list-disc list-inside space-y-2">
                        {rightsItems.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                      <p className="mt-4">
                        {t('privacy.rights.contact_info')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-6 mb-8 border-l-4 border-yellow-400">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <Bell className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('privacy.policy_changes.title')}</h3>
                      <p className="text-gray-600">
                        {t('privacy.policy_changes.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-sm text-gray-500 border-t border-gray-200 pt-6">
                  <p>
                    {t('privacy.last_update')} {new Date().toLocaleDateString(localStorage.getItem('i18nextLng') === 'en' ? 'en-US' : 'fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
}